import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Tanks from "@/pages/Tanks";
import Transaction360 from "@/pages/Transaction360";
import Approvals from "@/pages/Approvals";
import AuditTrail from "@/pages/AuditTrail";
import AdminUsers from "@/pages/AdminUsers";
import AIAssistant from "@/pages/AIAssistant";
import InventoryMovements from "@/pages/InventoryMovements";
import Exceptions from "@/pages/Exceptions";
import ModulePage from "@/pages/ModulePage";
import Portal from "@/pages/Portal";
import SupplierPortal from "@/pages/SupplierPortal";
import { RiskRadar, Periods, Notifications } from "@/pages/Extras";
import Configuration from "@/pages/Configuration";
import ReconciliationEngine from "@/pages/ReconciliationEngine";
import DataImport from "@/pages/DataImport";
import Forecast from "@/pages/Forecast";
import LandedCost from "@/pages/LandedCost";
import ProofOfDelivery from "@/pages/ProofOfDelivery";
import Help from "@/pages/Help";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Portal />;
  if (user.role === "supplier") return <SupplierPortal />;
  return <AppShell>{children}</AppShell>;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/transaction-360" element={<Protected><Transaction360 /></Protected>} />
      <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
      <Route path="/suppliers" element={<Protected><ModulePage moduleKey="suppliers" /></Protected>} />
      <Route path="/spas" element={<Protected><ModulePage moduleKey="spas" /></Protected>} />
      <Route path="/supplier-invoices" element={<Protected><ModulePage moduleKey="supplier-invoices" /></Protected>} />
      <Route path="/supplier-payments" element={<Protected><ModulePage moduleKey="supplier-payments" /></Protected>} />
      <Route path="/customer-payments" element={<Protected><ModulePage moduleKey="customer-payments" /></Protected>} />
      <Route path="/lprc-transfers" element={<Protected><ModulePage moduleKey="lprc-transfers" /></Protected>} />
      <Route path="/tanks" element={<Protected><Tanks /></Protected>} />
      <Route path="/inventory-movements" element={<Protected><InventoryMovements /></Protected>} />
      <Route path="/inventory-adjustments" element={<Protected><ModulePage moduleKey="inventory-adjustments" /></Protected>} />
      <Route path="/customers" element={<Protected><ModulePage moduleKey="customers" /></Protected>} />
      <Route path="/sales-orders" element={<Protected><ModulePage moduleKey="sales-orders" /></Protected>} />
      <Route path="/delivery-orders" element={<Protected><ModulePage moduleKey="delivery-orders" /></Protected>} />
      <Route path="/deliveries" element={<Protected><ModulePage moduleKey="deliveries" /></Protected>} />
      <Route path="/reconciliations" element={<Protected><ModulePage moduleKey="reconciliations" /></Protected>} />
      <Route path="/reconciliation-engine" element={<Protected><ReconciliationEngine /></Protected>} />
      <Route path="/pod" element={<Protected><ProofOfDelivery /></Protected>} />
      <Route path="/vehicles" element={<Protected><ModulePage moduleKey="vehicles" /></Protected>} />
      <Route path="/drivers" element={<Protected><ModulePage moduleKey="drivers" /></Protected>} />
      <Route path="/routes" element={<Protected><ModulePage moduleKey="routes" /></Protected>} />
      <Route path="/hsse" element={<Protected><ModulePage moduleKey="hsse" /></Protected>} />
      <Route path="/forecast" element={<Protected><Forecast /></Protected>} />
      <Route path="/landed-cost" element={<Protected><LandedCost /></Protected>} />
      <Route path="/help" element={<Protected><Help /></Protected>} />
      <Route path="/data-import" element={<Protected><DataImport /></Protected>} />
      <Route path="/config" element={<Protected><Configuration /></Protected>} />
      <Route path="/exceptions" element={<Protected><Exceptions /></Protected>} />
      <Route path="/risk" element={<Protected><RiskRadar /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/admin/periods" element={<Protected><Periods /></Protected>} />
      <Route path="/audit" element={<Protected><AuditTrail /></Protected>} />
      <Route path="/products" element={<Protected><ModulePage moduleKey="products" /></Protected>} />
      <Route path="/admin/users" element={<Protected><AdminUsers /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
